// Dashboards data layer (arc 101 slice 09, task #81) — the read side of the
// JSON-defined boards. `GET /v1/dashboards` (list, shared ∪ own), `GET
// /v1/dashboards/:id` (one board + its widgets + concrete layout), and the
// capability-gated pager `POST /v1/dashboards/:id/widgets/:wid/page` that feeds
// each data widget one page of rows. Read-only over `apiFetch`, reusing the dev
// session bearer + re-keying on the active account (org switcher, slice 02).
//
// The pager's widget-data resolver is the deferred empty-page seam server-side
// until the data plane lands (#83), so a board draws its SHAPE immediately and
// its widgets fill in once readers land — the renderer treats an empty page as a
// first-class state, not an error.

import type {
  DashboardDetailWire,
  DashboardScope,
  DashboardWidgetWire,
  DashboardWire,
  PageRequest,
  PageResponse,
  PageRow,
  WidgetFilter,
  WidgetSort,
} from '@developerz/domain';
import { DASHBOARD_SCOPES } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { ApiError, apiFetch, withCursor } from './api';
import { type Page, type PagedList, usePagedQuery } from './paged';
import { SESSION_TOKEN } from './session';

// The three board bodies below were the THIRD hand-written copy of a shape that
// `apps/api/src/routes/dashboards.ts` and `packages/mcp/src/army/dashboard.ts`
// each declared once more. They now derive from the single wire declaration
// (`packages/domain/src/wire/dashboard.ts`), so a field added there is a type
// error here rather than a column that silently stops rendering.
//
// ONE FIELD IS RESTATED IN EACH: `id`, as a plain `string`. The wire schema
// types it with the BRANDED `DashboardIdSchema` / `DashboardWidgetIdSchema`, and
// the brand cannot be carried across this boundary honestly or usefully:
//
// - `apiFetch` is a cast, not a parse (`api.ts`), so a brand asserted on a
//   `JSON.parse` result claims a validation nobody ran. The dashboard holds no
//   other branded id anywhere, and the same ids also reach these call sites as
//   plain route params.
// - `domain` contradicts itself on the widget half: `DashboardLayout.rows[]`
//   references a widget by `widgetId: z.string().min(1)` (unbranded) while
//   `DashboardWidgetWireSchema.id` is branded, so a board's own layout cannot
//   index its own widgets without a cast. Reconciling that is a `packages/domain`
//   change, not a dashboard one.
//
// Every OTHER field — names, nullability, the derived `scope`, `widget_count`,
// the layout and the widget specs — derives, which is the drift this seam exists
// to stop.

/** A board in the list — `DashboardResource` minus the id brand (see above). */
export type DashboardSummary = Readonly<Omit<DashboardWire, 'id'>> & { readonly id: string };

/** One widget on a board — `DashboardWidgetResource` minus the id brand. */
export type DashboardWidgetView = Readonly<Omit<DashboardWidgetWire, 'id'>> & {
  readonly id: string;
};

/**
 * A board's full detail — `DashboardDetailResource`: the summary + the concrete
 * 12-col layout + every widget spec. `layout.rows[].widgetId` references
 * `widgets[].id`.
 */
export type DashboardDetail = Readonly<Omit<DashboardDetailWire, 'id' | 'widgets'>> & {
  readonly id: string;
  readonly widgets: readonly DashboardWidgetView[];
};

export const DASHBOARDS_QUERY_KEY = 'dashboards';
export const WIDGET_PAGE_QUERY_KEY = 'widget_page';

/** ONE page of the account's dashboards at `cursor` (sorted by name). Idle with
 * no session. Hand it to `usePagedQuery` ({@link useDashboards}). */
export function useDashboardPage(
  cursor: () => string | null,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<Page<DashboardSummary>>(() => ({
    queryKey: [DASHBOARDS_QUERY_KEY, 'list', token, activeAccountId(), cursor()],
    enabled: token !== null,
    queryFn: async () => {
      const page = await apiFetch<Page<DashboardSummary>>(
        withCursor('/v1/dashboards', cursor()),
        token,
      );
      return { data: page.data, next_cursor: page.next_cursor ?? null };
    },
  }));
}

/**
 * The account's dashboards, walked WHOLE (`exhaust`, the `lib/repos.ts` idiom).
 * The route pages at the grammar's default 50 and mints `next_cursor`; reading
 * page one only (19-16) left board fifty-one unlisted, so it could not be opened,
 * renamed or deleted from the index, and the create form's name-taken check
 * (`boardNameTaken`) could not see it. After a write, `reload()` it.
 */
export function useDashboards(token: string | null = SESSION_TOKEN): PagedList<DashboardSummary> {
  return usePagedQuery<DashboardSummary>((cursor) => useDashboardPage(cursor, token), {
    exhaust: true,
  });
}

/** One board + its widgets. `id` is an accessor so a route-param change re-keys. */
export function useDashboard(id: () => string, token: string | null = SESSION_TOKEN) {
  return createQuery<DashboardDetail | null>(() => ({
    queryKey: [DASHBOARDS_QUERY_KEY, 'detail', id(), token, activeAccountId()],
    enabled: token !== null && id() !== '',
    // A 404 is a STATE, not a failure — the `useRepoPolicy` idiom. A board id in
    // a stale link, a shared link to someone else's personal board, or a board
    // deleted since the tab was opened all land here, and letting the throw
    // through rendered "Something went wrong." with a retry that can never
    // succeed, while the route's own "no such dashboard" copy sat unreachable.
    queryFn: async () => {
      try {
        return await apiFetch<DashboardDetail>(`/v1/dashboards/${id()}`, token);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  }));
}

/** The negotiated pager state (#977) — one sort + one filter term, `null` = unset. */
export type WidgetPageControls = {
  readonly sort: WidgetSort | null;
  readonly filter: WidgetFilter | null;
};

export const NO_WIDGET_PAGE_CONTROLS: WidgetPageControls = { sort: null, filter: null };

/** Build the `PageRequest` body. Empty fields are OMITTED — a zero-capability
 * widget still posts exactly `{}`, byte-compatible with the pre-#977 pager. */
export function widgetPageBody(controls: WidgetPageControls, cursor: string | null): PageRequest {
  return {
    ...(cursor !== null ? { page: { cursor } } : {}),
    ...(controls.sort !== null ? { sort: [controls.sort] } : {}),
    ...(controls.filter !== null ? { filters: [controls.filter] } : {}),
  };
}

/** One page of a widget's rows at `cursor` under the negotiated `controls` (#977
 * — both reactive, so a control change or load-more re-keys the query). Answers
 * the canonical `{ data, next_cursor }` slice — the wire's `{ rows, cursor }` is
 * remapped here so `usePagedQuery` (refetch-dedupe, sticky cursor, reset) accumulates it. */
export function useWidgetPage(
  dashboardId: string,
  widgetId: string,
  controls: () => WidgetPageControls = () => NO_WIDGET_PAGE_CONTROLS,
  cursor: () => string | null = () => null,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<Page<PageRow>>(() => ({
    queryKey: [
      WIDGET_PAGE_QUERY_KEY,
      dashboardId,
      widgetId,
      controls(),
      cursor(),
      token,
      activeAccountId(),
    ],
    enabled: token !== null,
    queryFn: async () => {
      const page = await apiFetch<PageResponse>(
        `/v1/dashboards/${dashboardId}/widgets/${widgetId}/page`,
        token,
        { method: 'POST', body: JSON.stringify(widgetPageBody(controls(), cursor())) },
      );
      return { data: page.rows, next_cursor: page.cursor ?? null };
    },
  }));
}

/** A scope section for the dashboard nav (the "router by scope"). */
export type DashboardScopeGroup = {
  readonly scope: DashboardScope;
  readonly dashboards: readonly DashboardSummary[];
};

/**
 * Group boards into ordered scope sections — `org` (shared) → `user` (personal) →
 * `custom` (API/MCP-authored) — dropping empty sections. This is the scope router:
 * one flat list in, the nav's scope-grouped structure out, with the derived
 * `scope` (never a stored column) as the sole key.
 */
export function groupDashboardsByScope(
  list: readonly DashboardSummary[],
): readonly DashboardScopeGroup[] {
  return DASHBOARD_SCOPES.map((scope) => ({
    scope,
    dashboards: list.filter((d) => d.scope === scope),
  })).filter((g) => g.dashboards.length > 0);
}
